import React, { useMemo, useState, createContext, useContext } from 'react';
import {
  firmar,
  firmarSat,
  SeleccionarArchivo,
  SeleccionarArchivoEFirma,
} from '../../../../../services/electronicSignature';
import {
  SelccionarArchivo,
  setSignaturesList,
  getPkcs7 as getPkcs7MultipleSign,
} from '../../../../../services/multipleElectronicSignature';

const ElectronicSignatureProcessContext = createContext();
ElectronicSignatureProcessContext.displayName = 'ElectronicSignatureProcessContext';

const ElectronicSignatureProcessProvider = ({
  children,
  elementId,
  isOnlyOneSignaturePerUserActivated = false,
}) => {
  const [isElectronicSignatureModalSowing, setIsElectronicSignatureModalSowing] = useState(false);
  const [electronicSignaturesList, setElectronicSignaturesList] = useState([]);
  const [initialElectronicSignaturesList, setInitialElectronicSignaturesList] = useState([]);
  const [isMultipleSignature, setIsMultipleSignature] = useState(false);
  const [documentToSign, setDocumentToSign] = useState(null);
  const [singleElectronicSignature, setSingleElectronicSignature] = useState(null);
  const isThereASignature = Boolean(electronicSignaturesList.length);

  const initialElectronicSignaturesIdList = initialElectronicSignaturesList.map(
    (initialElectronicSignature) => initialElectronicSignature.id
  );
  const electronicSignaturesRecentlyAddedList = electronicSignaturesList.filter(
    (electronicSignature) => !initialElectronicSignaturesIdList.includes(electronicSignature.id)
  );
  const isThereASignatureRecentlyAdded = Boolean(electronicSignaturesRecentlyAddedList.length);

  const isReadyToCreatePkcs7 = useMemo(() => {
    const hasDocumentToSignBeenUploaded = Boolean(documentToSign);
    if (isMultipleSignature) {
      const minimumNumberOfSignatures = isOnlyOneSignaturePerUserActivated ? 1 : 2;
      const isThereMoreThanOneSignature =
        electronicSignaturesList.length >= minimumNumberOfSignatures;
      return isThereMoreThanOneSignature && hasDocumentToSignBeenUploaded;
    }
    const hasSingleElectronicSignatureBeenAdded = Boolean(singleElectronicSignature);
    return hasSingleElectronicSignatureBeenAdded && hasDocumentToSignBeenUploaded;
  }, [electronicSignaturesList, isMultipleSignature, documentToSign, singleElectronicSignature]);

  const onCloseModal = () => {
    setIsElectronicSignatureModalSowing(false);
  };

  const onAddElectronicSignature = (
    electronicSignatureToAdd,
    isOnlyOneSignaturePerUserActivated = true
  ) => {
    const { multiSign, singleSign } = electronicSignatureToAdd;
    if (Boolean(multiSign)) {
      setElectronicSignaturesList((previousState) => {
        const previousSignatureList = isOnlyOneSignaturePerUserActivated
          ? initialElectronicSignaturesList
          : previousState;
        return [...previousSignatureList, { ...multiSign }];
      });
    }
    setSingleElectronicSignature(singleSign);
  };

  const onDelteElectronicSignatureById = (id) => {
    const newElectronicSignaturesList = electronicSignaturesList.filter(
      (signatureItem) => signatureItem.id !== id
    );
    const newInitialElectronicSignaturesList = initialElectronicSignaturesList.filter(
      (signatureItem) => signatureItem.id !== id
    );
    setElectronicSignaturesList(newElectronicSignaturesList);
    setInitialElectronicSignaturesList(newInitialElectronicSignaturesList);
  };

  const onAddDocumentToSign = async (document) => {
    setDocumentToSign(document[0]);
  };

  const handleLoadDocumentToSignToElectronicSignature = async () => {
    if (!documentToSign) {
      throw new Error('Error uploading file to sign');
    }
    const isMultipleSignUploaded = await SelccionarArchivo({ target: { files: [documentToSign] } });
    const isSingleSingEfirmaUploaded = await SeleccionarArchivoEFirma(documentToSign);
    const isSingleSingFirelUploaded = await SeleccionarArchivo(documentToSign);
    return isMultipleSignUploaded && isSingleSingEfirmaUploaded && isSingleSingFirelUploaded;
  };

  const getPkcs7SingleSign = async () => {
    if (singleElectronicSignature.signaturetype === 'sat') {
      return await firmarSat(singleElectronicSignature.password);
    }
    return await firmar(singleElectronicSignature.password);
  };

  const getPkcs7 = async () => {
    const isDocumentToSignLoaded = await handleLoadDocumentToSignToElectronicSignature();
    if (!isDocumentToSignLoaded) {
      throw new Error('File to sign has not been uploaded');
    }
    let pkcs7 = '';
    if (isMultipleSignature) {
      setSignaturesList(electronicSignaturesList);
      pkcs7 = await getPkcs7MultipleSign();
    } else {
      pkcs7 = await getPkcs7SingleSign();
    }
    return pkcs7;
  };

  const onResetSignatures = () => {
    setElectronicSignaturesList([]);
    setInitialElectronicSignaturesList([]);
    setSingleElectronicSignature(null);
  };

  const handleSetInitialElectronicSignaturesList = (electronicSignaturesListArgument = []) => {
    setElectronicSignaturesList(electronicSignaturesListArgument);
    setInitialElectronicSignaturesList(electronicSignaturesListArgument);
  };

  const contextValue = {
    isElectronicSignatureModalSowing,
    setIsElectronicSignatureModalSowing,
    electronicSignaturesList,
    initialElectronicSignaturesList,
    electronicSignaturesRecentlyAddedList,
    onCloseModal,
    onAddElectronicSignature,
    isMultipleSignature,
    setIsMultipleSignature,
    getPkcs7,
    isReadyToCreatePkcs7,
    documentToSign,
    setDocumentToSign,
    onAddDocumentToSign,
    onDelteElectronicSignatureById,
    singleElectronicSignature,
    setSingleElectronicSignature,
    isThereASignature,
    onResetSignatures,
    elementId,
    handleSetInitialElectronicSignaturesList,
    setElectronicSignaturesList,
    isThereASignatureRecentlyAdded,
  };

  return (
    <ElectronicSignatureProcessContext.Provider value={contextValue}>
      {children}
    </ElectronicSignatureProcessContext.Provider>
  );
};

export const useElectronicSignatureProcessContext = () => {
  const context = useContext(ElectronicSignatureProcessContext);
  if (typeof context === 'undefined') {
    throw new Error(
      'To use useElectronicSignatureProcessContext you must wrap inside a ElectronicSignatureProcessProvider'
    );
  }
  return context;
};

export default ElectronicSignatureProcessProvider;
